import type { TFunction } from 'i18next'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'
import { formatDateTime } from '@/lib/datetime'
import { formatMoney } from '@/lib/money'
import { cn } from '@/lib/cn'

import { useAdminMutation } from '../api'
import { AdminHeader } from '../AdminLayout'
import { CollectionEditor } from '../components/CollectionEditor'
import { MoneyField, SelectField, TextField } from '../components/fields'
import {
  DefineTableType,
  OrderBracelets,
  OrderConfirmation,
  PaidTablePrompt,
} from '../components/OrderFollowUp'
import { AtDoorSettlement } from '../components/AtDoorSettlement'
import { MergeIntoEntry, OrderSupplements } from '../components/OrderSupplements'
import { TableRequestOrderPanel } from '../components/TableRequestFollowUp'
import { useAdminContext } from '../session'
import { useCollection } from '../useCollection'
import type { AdminOrder } from '../types'

/**
 * Les moyens d'encaissement hors ligne. Le libellé part dans l'audit, la
 * valeur dans le règlement (`payments.method`) : la clôture et les finances
 * ventilent par elle. PayPal en tête — c'est par lien PayPal que la plupart
 * des clients règlent, et il manquait de la liste.
 */
const OFFLINE_METHODS = ['paypal', 'transfer', 'cash', 'manual']

/**
 * Les commandes : consulter, encaisser hors ligne, rembourser.
 *
 * Deux gestes sensibles, et ils sont sensibles pour la même raison : ils
 * touchent à de l'argent sans qu'un prestataire de paiement l'ait confirmé.
 *
 * * **Encaisser hors ligne** dit « j'ai reçu l'argent ». Le moyen et la
 *   référence partent dans le journal d'audit — c'est ce qui permet, un mois
 *   plus tard, de savoir qui a encaissé quoi et sur quelle preuve.
 * * **Rembourser** exige un motif d'au moins cinq caractères. Ce n'est pas
 *   une formalité : un remboursement sans explication est indéfendable
 *   devant un client comme devant un comptable.
 *
 * Rien ici ne modifie une commande : on ne réécrit pas ce qui a été vendu.
 */
export function AdminOrdersPage() {
  const { t, i18n } = useTranslation()
  const { eventId, event } = useAdminContext()
  const currency = event?.currency ?? 'EUR'

  useEffect(() => {
    document.title = t('admin.section.orders')
  }, [t])

  // Deux filtres, parce que ce sont les deux questions qu'on se pose en
  // pointant les virements : « où en sont les tables ? » et « qui n'a pas
  // payé ? ».
  const [category, setCategory] = useState('')
  const [settlement, setSettlement] = useState('')
  // Le soir J, les ventes de la caisse et des tables noient les commandes en
  // ligne qu'on cherche à pointer — et inversement le lendemain.
  const [channel, setChannel] = useState('')
  // `?q=MN26-000123` : on arrive ici depuis une demande de table acceptée,
  // pour retrouver la commande qu'elle a créée sans la chercher dans la liste.
  const [params] = useSearchParams()
  const search = params.get('q') ?? ''
  // Une table qu'on vient d'encaisser hors ligne : « Envoyer la
  // confirmation maintenant ? ». Tenue ici, au-dessus de la liste, pour
  // survivre à la fermeture de la fiche (et à un filtre « non réglées »).
  const [paidTable, setPaidTable] = useState<{ id: string; order_number: string } | null>(null)

  const orders = useCollection<AdminOrder>({
    key: 'admin-orders',
    path: `/admin/events/${eventId}/orders`,
    eventId,
    query: {
      category: category || undefined,
      settlement: settlement || undefined,
      channel: channel || undefined,
      q: search || undefined,
    },
  })

  // Nul : le serveur a retiré le montant (`orders.amount.read`, la porte).
  // Les écrans ne l'affichent alors pas du tout ; le tiret ne sert que de
  // filet, jamais de « 0 € » trompeur.
  function money(minor: number | null | undefined): string {
    if (minor === null || minor === undefined) return '—'
    return formatMoney({ amount_minor: minor, currency }, i18n.language)
  }

  return (
    <>
      <AdminHeader
        title={t('admin.section.orders')}
        description={t('admin.orders.description')}
      />

      <div className="mb-4 grid gap-5 sm:grid-cols-3">
        <SelectField
          label={t('admin.orders.filterCategory')}
          value={category}
          onChange={setCategory}
          options={[
            { value: '', label: t('admin.orders.filterAll') },
            ...['admission', 'table', 'food'].map((value) => ({
              value,
              label: t(`admin.orders.category.${value}`),
            })),
          ]}
        />
        <SelectField
          label={t('admin.orders.filterSettlement')}
          value={settlement}
          onChange={setSettlement}
          options={[
            { value: '', label: t('admin.orders.filterAll') },
            ...['complete', 'partial', 'unpaid', 'overdue'].map((value) => ({
              value,
              label: t(`admin.orders.settlement.${value}`),
            })),
          ]}
        />
        <SelectField
          label={t('admin.orders.filterChannel')}
          value={channel}
          onChange={setChannel}
          options={[
            { value: '', label: t('admin.orders.filterAll') },
            ...['online', 'pos', 'admin'].map((value) => ({
              value,
              label: t(`admin.orders.channel.${value}`),
            })),
          ]}
        />
      </div>

      {search ? (
        <p className="mb-4 text-sm text-content-secondary">
          {t('admin.orders.filteredOn', { query: search })}{' '}
          <Link to="." className="text-champagne-300 underline">
            {t('admin.orders.clearFilter')}
          </Link>
        </p>
      ) : null}

      {paidTable ? (
        <PaidTablePrompt
          key={paidTable.id}
          order={paidTable}
          onClose={() => setPaidTable(null)}
        />
      ) : null}

      <CollectionEditor<AdminOrder>
        title={t('admin.orders.list')}

        description={t('admin.orders.listHint', { count: orders.total })}
        items={orders.items}
        busy={orders.busy}
        error={orders.error}
        emptyLabel={t('admin.orders.empty')}
        // Une commande ne se saisit pas ici : elle naît d'un achat, ou d'une
        // demande de table acceptée (« Demandes de table »).
        createLabel={null}
        summarize={(order) => ({
          title: order.payment_reference
            ? `${order.payment_reference} · ${order.order_number}`
            : order.order_number,
          subtitle: [
            order.customer_name ?? order.customer_email ?? t('admin.orders.noCustomer'),
            formatDateTime(order.created_at, i18n.language, event?.timezone ?? 'UTC'),
          ].join(' · '),
          // Le total de la réservation : commande + plats ajoutés ensuite.
          // Rien pour qui ne voit pas les montants : pas même un tiret.
          ...(order.amounts_visible === false
            ? {}
            : { value: money(order.grand_total_minor ?? order.total_minor) }),
          dimmed: order.status === 'cancelled' || order.status === 'expired',
          badges: [
            {
              label: t(`admin.orders.status.${order.status}`),
              tone:
                order.status === 'paid'
                  ? ('success' as const)
                  : order.status === 'pending_payment'
                    ? ('warning' as const)
                    : order.status === 'refunded' || order.status === 'partially_refunded'
                      ? ('danger' as const)
                      : ('neutral' as const),
            },
            {
              label: t(`admin.orders.settlement.${order.settlement}`),
              tone:
                order.settlement === 'complete'
                  ? ('success' as const)
                  : order.settlement === 'overdue'
                    ? ('danger' as const)
                    : order.settlement === 'partial'
                      ? ('warning' as const)
                      : ('neutral' as const),
            },
            { label: t(`admin.orders.category.${order.category}`) },
            // Réglée à l'entrée, ou qui le sera : la porte et le bureau
            // le lisent dans la liste, sans ouvrir la fiche.
            ...(atDoorBadge(order, money, t) ?? []),
            ...(order.payment_plan === 'installments'
              ? [
                  {
                    label: t('admin.orders.inInstalments', {
                      count: order.installments.length,
                    }),
                  } as const,
                ]
              : []),
            ...(order.channel === 'pos'
              ? [{ label: t('admin.orders.onsite') } as const]
              : []),
            // Une table payée reste « payée » ; ce qui attend, ce sont les
            // plats ajoutés — on le montre à côté, sans changer son statut.
            ...((order.supplements_due_minor ?? 0) > 0
              ? [
                  {
                    label: t('admin.orders.supplements.dueBadge', {
                      amount: money(order.supplements_due_minor ?? 0),
                    }),
                    tone: 'warning' as const,
                  },
                ]
              : []),
            // Une table réglée dont la confirmation n'est pas encore partie.
            ...(order.status === 'paid' &&
            order.confirmation_kind === 'table' &&
            !order.confirmation_sent_at
              ? [{ label: t('admin.orders.confirmation.badge'), tone: 'warning' as const }]
              : []),
            ...(order.needs_table_type
              ? [{ label: t('admin.orders.tableType.badge'), tone: 'warning' as const }]
              : []),
            ...((order.bracelets_wanted ?? 0) > 0
              ? [
                  {
                    label: t('admin.orders.bracelets.badge', {
                      count: order.bracelets_wanted ?? 0,
                    }),
                  } as const,
                ]
              : []),
            ...(order.merged_into_order_number
              ? [
                  {
                    label: t('admin.orders.merge.badge', {
                      number: order.merged_into_order_number,
                    }),
                  } as const,
                ]
              : []),
          ],
        })}
        renderForm={(order, close) =>
          order ? (
            <OrderPanel
              order={order}
              eventId={eventId}
              currency={currency}
              money={money}
              onDone={close}
              onPaidTable={setPaidTable}
            />
          ) : null
        }
      />
    </>
  )
}

type Money = (minor: number | null | undefined) => string

/**
 * « À régler à l'entrée : 480,00 € » ou « Réglée à l'entrée » — le badge de
 * la liste. Sans le droit de voir les montants, le premier perd son chiffre :
 * la porte sait qu'il reste quelque chose à encaisser, pas combien la table
 * a coûté (le montant à demander s'affiche au scan).
 */
function atDoorBadge(
  order: AdminOrder,
  money: Money,
  t: TFunction,
): { label: string; tone: 'warning' | 'success' }[] | null {
  if (order.settled_at_door) {
    return [{ label: t('admin.orders.atDoor.settledBadge'), tone: 'success' }]
  }
  if (order.pays_at_door && order.status === 'pending_payment') {
    return [
      {
        label:
          order.amount_due_minor === null || order.amount_due_minor === undefined
            ? t('admin.orders.atDoor.due')
            : t('admin.orders.atDoor.dueAmount', { amount: money(order.amount_due_minor) }),
        tone: 'warning',
      },
    ]
  }
  return null
}

function OrderPanel({
  order,
  eventId,
  currency,
  money,
  onDone,
  onPaidTable,
}: {
  order: AdminOrder
  eventId: string | undefined
  currency: string
  money: Money
  onDone: () => void
  /** Une table vient d'être réglée : proposer d'envoyer sa confirmation. */
  onPaidTable: (order: { id: string; order_number: string }) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [method, setMethod] = useState(OFFLINE_METHODS[0] ?? 'cash')
  const [reference, setReference] = useState('')
  const [refundAmount, setRefundAmount] = useState<number | null>(null)
  const [refundReason, setRefundReason] = useState('')
  const [cancelReason, setCancelReason] = useState('')
  const [confirming, setConfirming] = useState<'cancel' | 'delete' | null>(null)
  // Cochée d'office : sans entrée, la personne ne mangera pas ses plats. On
  // laisse la décocher — une entrée refaite à part, par exemple, garde ses
  // repas en attendant qu'on les rattache.
  const [cancelLinked, setCancelLinked] = useState(true)
  const linked = order.linked_orders ?? []
  const linkedUnpaid = linked.filter((row) => row.cancellable)
  const linkedPaid = linked.filter((row) => row.refund_due)
  // De l'argent est-il entré ? Le statut et le montant encaissé le disent :
  // c'est ce qui décide si la suppression retire une ligne de rien ou une
  // recette du rapport de clôture.
  // Sans les montants (la porte), l'état dérivé du serveur dit la même chose.
  const amountsVisible = order.amounts_visible !== false
  const settled =
    order.status === 'paid' ||
    (amountsVisible ? (order.paid_minor ?? 0) > 0 : order.settlement !== 'unpaid')
  // Ce qui a été encaissé et pas encore rendu : tant qu'il en reste, la
  // commande ne s'annule pas — elle se rembourse d'abord.
  const keptMinor = Math.max((order.paid_minor ?? 0) - (order.refunded_minor ?? 0), 0)
  // L'argent des plats ajoutés compte aussi : une commande dont un supplément
  // est réglé ne s'annule pas — on rembourse le supplément d'abord.
  const keptSupplements = (order.supplements ?? []).reduce(
    (total, row) => total + Math.max((row.paid_minor ?? 0) - (row.refunded_minor ?? 0), 0),
    0,
  )

  const invalidate = [['admin-orders', eventId], ['admin-readiness', eventId ?? '']]

  const markPaid = useAdminMutation<AdminOrder, { method: string; reference: string | null }>(
    'post',
    () => `/admin/events/${eventId}/orders/${order.id}/mark-paid`,
    eventId,
    invalidate,
  )
  const refund = useAdminMutation<unknown, { amount_minor: number; reason: string }>(
    'post',
    () => `/admin/events/${eventId}/orders/${order.id}/refund`,
    eventId,
    invalidate,
  )
  const sendLink = useAdminMutation<{ message: string }, Record<string, never>>(
    'post',
    () => `/admin/events/${eventId}/orders/${order.id}/send-payment-link`,
    eventId,
    invalidate,
  )
  const cancel = useAdminMutation<AdminOrder, { reason: string; cancel_linked: boolean }>(
    'post',
    () => `/admin/events/${eventId}/orders/${order.id}/cancel`,
    eventId,
    invalidate,
  )
  // `force` lève le garde-fou d'une commande déjà encaissée. Le second clic
  // n'est pas une formalité : les recettes se recalculent sans elle, et le
  // rapport de clôture change.
  const remove = useAdminMutation<{ message: string }, { force?: boolean }>(
    'delete',
    (variables) =>
      `/admin/events/${eventId}/orders/${order.id}${variables.force ? '?force=true' : ''}`,
    eventId,
    invalidate,
  )

  const busy =
    markPaid.isPending ||
    refund.isPending ||
    cancel.isPending ||
    remove.isPending ||
    sendLink.isPending
  const error =
    markPaid.error ?? refund.error ?? cancel.error ?? remove.error ?? sendLink.error

  // Ce qui reste remboursable : on ne rend jamais plus que ce qui a été payé.
  // Le bouton suit l'avis du serveur (`order.refundable`) : proposer un
  // remboursement qu'il refusera au clic, c'est faire saisir un motif pour
  // rien.
  const refundable =
    order.refundable && amountsVisible
      ? Math.max((order.paid_minor ?? 0) - (order.refunded_minor ?? 0), 0)
      : 0

  return (
    <div>
      <ul className="divide-y divide-line-subtle text-sm">
        {/* Les lignes d'origine ; celles des suppléments vivent dans leur lot,
            plus bas, avec leur propre règlement. */}
        {order.items
          .filter((item) => !item.supplement_sequence)
          .map((item, index) => (
          <li key={item.id ?? index} className="flex items-baseline justify-between gap-4 py-2">
            <span className="min-w-0 break-words text-content-primary">
              {item.quantity}× {item.label}
            </span>
            {amountsVisible ? (
              <span className="shrink-0 tabular-nums text-content-secondary">
                {money(item.line_total_minor)}
              </span>
            ) : null}
          </li>
        ))}
      </ul>

      <AtDoorSettlement
        pending={order.status === 'pending_payment'}
        paysAtDoor={Boolean(order.pays_at_door)}
        dueMinor={order.amount_due_minor}
        settledAtDoor={Boolean(order.settled_at_door)}
        method={order.settled_method}
        by={order.settled_by}
        paidAt={order.paid_at}
        money={money}
      />

      <dl className="mt-4 space-y-1 text-sm">
        {amountsVisible ? (
          <>
            <Line label={t('admin.orders.subtotal')} value={money(order.subtotal_minor)} />
            {(order.discount_minor ?? 0) > 0 ? (
              <Line
                label={t('admin.orders.discount')}
                value={`− ${money(order.discount_minor)}`}
              />
            ) : null}
            {(order.surcharge_minor ?? 0) > 0 ? (
              <Line label={t('admin.orders.surcharge')} value={money(order.surcharge_minor)} />
            ) : null}
            <Line label={t('admin.orders.total')} value={money(order.total_minor)} strong />
            <Line label={t('admin.orders.paid')} value={money(order.paid_minor)} />
            {(order.refunded_minor ?? 0) > 0 ? (
              <Line label={t('admin.orders.refunded')} value={money(order.refunded_minor)} />
            ) : null}
          </>
        ) : (
          <p className="text-caption text-content-muted">{t('admin.orders.amountsHidden')}</p>
        )}
        <Line
          label={t('admin.orders.issued')}
          value={t('admin.orders.issuedValue', {
            tickets: order.tickets_issued,
            attendees: order.attendees_issued,
          })}
        />
        {/* Des plats commandés seuls sont rattachés à l'entrée du client :
            l'équipe voit d'où vient l'entrée, et depuis l'entrée, les repas. */}
        {order.admission_order_number ? (
          <Line
            label={t('admin.orders.linkedAdmission')}
            value={order.admission_order_number}
          />
        ) : null}
        {order.linked_orders && order.linked_orders.length > 0 ? (
          <Line
            label={t('admin.orders.linkedFood')}
            value={order.linked_orders.map((row) => row.order_number).join(', ')}
          />
        ) : null}
        {(order.supplements ?? []).length > 0 && amountsVisible ? (
          <>
            <Line
              label={t('admin.orders.supplements.grandTotal')}
              value={money(order.grand_total_minor ?? order.total_minor)}
              strong
            />
            {(order.supplements_due_minor ?? 0) > 0 ? (
              <Line
                label={t('admin.orders.supplements.dueLabel')}
                value={money(order.supplements_due_minor ?? 0)}
              />
            ) : null}
          </>
        ) : null}
      </dl>

      <OrderSupplements order={order} eventId={eventId} currency={currency} money={money} />

      <MergeIntoEntry
        order={order}
        eventId={eventId}
        onMerged={(entry) => {
          onDone()
          // On rouvre la réservation, où vivent désormais les plats.
          navigate(`?q=${encodeURIComponent(entry.order_number)}`)
        }}
      />

      {error instanceof ApiError ? (
        <p className="mt-4 text-sm text-state-danger">{error.message}</p>
      ) : null}

      {/* Une table sur mesure : ses convives et sa confirmation (QR, noms),
          à envoyer une fois la commande réglée. Rien pour une autre commande.
          Une table acceptée avant la mise à jour y trouve aussi « Créer la
          réservation de table ». */}
      {(order.channel === 'admin' && order.category === 'table') || order.needs_reservation ? (
        <TableRequestOrderPanel orderId={order.id} />
      ) : null}

      {/* Une ligne de table sans réservation : absente du placement. */}
      <DefineTableType order={order} />

      {/* Réglée : envoyer, ou renvoyer, la confirmation au client. */}
      <OrderConfirmation order={order} />

      <OrderBracelets order={order} />

      <InstalmentSchedule
        order={order}
        eventId={eventId}
        money={money}
        onDone={onDone}
        onPaidTable={onPaidTable}
      />

      {/* Annuler et supprimer, tout en bas et separes du reste : ce sont les
          deux gestes qu on ne rattrape pas. Une commande payee n apparait pas
          ici — elle se rembourse, ce qui garde les deux ecritures. */}
      {order.status !== 'paid' && order.status !== 'cancelled' ? (
        <section className="mt-8 border-t border-line-subtle pt-5">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.orders.dangerZone')}
          </p>

          {confirming === 'cancel' ? (
            <div className="mt-3 space-y-3">
              <TextField
                label={t('admin.orders.cancelReason')}
                hint={t('admin.orders.cancelReasonHint')}
                value={cancelReason}
                onChange={setCancelReason}
              />
              {order.customer_email ? (
                <p className="text-caption text-content-muted">
                  {t('admin.orders.cancelSendsEmail', { email: order.customer_email })}
                </p>
              ) : null}
              {/* Les repas commandés en citant cette entrée : sans elle, la
                  personne n'entre pas. Les non réglés partent avec (case
                  cochée), les réglés restent — l'argent se rend à la main. */}
              {linkedUnpaid.length > 0 ? (
                <label className="flex items-start gap-3 rounded-md border border-line-subtle px-3 py-2.5">
                  <input
                    type="checkbox"
                    checked={cancelLinked}
                    onChange={() => setCancelLinked((value) => !value)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm text-content-primary">
                      {t('admin.orders.cancelLinked')}
                    </span>
                    <span className="mt-0.5 block break-words text-xs text-content-muted">
                      {linkedUnpaid.map((row) => row.order_number).join(', ')}
                    </span>
                  </span>
                </label>
              ) : null}
              {linkedPaid.length > 0 ? (
                <div className="rounded-md border border-line-subtle px-3 py-2.5 text-sm">
                  <p className="text-content-secondary">{t('admin.orders.linkedPaidTitle')}</p>
                  <ul className="mt-1 space-y-1">
                    {linkedPaid.map((row) => (
                      <li key={row.id} className="flex flex-wrap items-baseline gap-x-2">
                        <Link
                          to={`?q=${encodeURIComponent(row.order_number)}`}
                          onClick={onDone}
                          className="text-champagne-300 underline"
                        >
                          {row.order_number}
                        </Link>
                        <span className="text-xs text-content-muted">
                          {t('admin.orders.linkedPaidToRefund')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={busy || cancelReason.trim().length < 3}
                  onClick={() =>
                    cancel.mutate(
                      {
                        reason: cancelReason.trim(),
                        cancel_linked: cancelLinked && linkedUnpaid.length > 0,
                      },
                      { onSuccess: onDone },
                    )
                  }
                >
                  {t('admin.orders.confirmCancel')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                  {t('admin.project.cancel')}
                </Button>
              </div>
            </div>
          ) : confirming === 'delete' ? (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="text-sm text-content-secondary">
                {/* L'écran sait déjà si de l'argent est entré : la conséquence
                    s'annonce AVANT le clic, pas dans une erreur rouge qu'il
                    faudrait provoquer pour voir apparaître un second bouton. */}
                {settled
                  ? t('admin.orders.deletePaidConfirm')
                  : t('admin.orders.deleteConfirm')}
                {order.customer_email
                  ? ` ${t('admin.orders.cancelSendsEmail', { email: order.customer_email })}`
                  : ''}
                {/* Le serveur applique ici la règle d'office : effacée,
                    l'entrée ne pourrait plus dire à qui étaient ces repas. */}
                {linkedUnpaid.length > 0 || linkedPaid.length > 0
                  ? ` ${t('admin.orders.deleteLinkedHint')}`
                  : ''}
              </span>
              <Button
                variant={settled ? 'ember' : 'champagne'}
                size="sm"
                disabled={busy}
                onClick={() =>
                  remove.mutate({ force: settled }, { onSuccess: onDone })
                }
              >
                {t('admin.project.deleteYes')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                {t('admin.project.cancel')}
              </Button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {/* Un acompte encaissé se rend avant d'annuler : le serveur le
                  refuse, l'écran le dit d'avance plutôt qu'au clic. */}
              <Button
                variant="outline"
                size="sm"
                disabled={busy || keptMinor > 0 || keptSupplements > 0}
                onClick={() => setConfirming('cancel')}
              >
                {t('admin.orders.cancelOrder')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => setConfirming('delete')}
              >
                {t('admin.orders.deleteOrder')}
              </Button>
            </div>
          )}

          {keptMinor > 0 ? (
            <p className="mt-2 text-caption text-state-danger">
              {t('admin.orders.refundBeforeCancel', { amount: money(keptMinor) })}
            </p>
          ) : null}
          {keptSupplements > 0 ? (
            <p className="mt-2 text-caption text-state-danger">
              {t('admin.orders.supplements.refundBeforeCancel', {
                amount: money(keptSupplements),
              })}
            </p>
          ) : null}
          <p className="mt-2 text-caption text-content-muted">
            {t('admin.orders.dangerHint')}
          </p>
        </section>
      ) : null}

      {/* L ordre reel des gestes : on relit la commande, on envoie le lien,
          puis on constate le reglement. Une commande expiree reste
          reglable tant que sa place est libre : le serveur reverifie le
          stock et refuse (STOCK_NO_LONGER_AVAILABLE) sinon. */}
      {order.status === 'expired' ? (
        <p className="mt-6 rounded-md border border-line-subtle px-3 py-2 text-xs text-content-muted">
          {t('admin.orders.expiredHint')}
        </p>
      ) : null}

      {/* Qui règle à l'entrée ne reçoit pas de lien : le serveur le refuse
          (ORDER_PAYS_AT_DOOR), le bouton n'a donc pas lieu d'être. */}
      {(order.status === 'pending_payment' || order.status === 'expired') &&
      !order.pays_at_door ? (
        <section className="mt-6 border-t border-line-subtle pt-5">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.orders.sendLink')}
          </p>
          <p className="mt-1 text-xs text-content-muted">{t('admin.orders.sendLinkHint')}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            disabled={busy}
            onClick={() => sendLink.mutate({})}
          >
            {sendLink.isSuccess
              ? t('admin.orders.sendLinkAgain')
              : t('admin.orders.sendLinkAction')}
          </Button>
        </section>
      ) : null}

      {order.status === 'pending_payment' || order.status === 'expired' ? (
        <section className="mt-6 border-t border-line-subtle pt-5">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.orders.markPaid')}
          </p>
          <p className="mt-1 text-xs text-content-muted">{t('admin.orders.markPaidHint')}</p>
          <div className="mt-3 grid gap-5 sm:grid-cols-2">
            <SelectField
              label={t('admin.orders.method')}
              value={method}
              onChange={setMethod}
              options={OFFLINE_METHODS.map((value) => ({
                value,
                label: t(`admin.orders.methods.${value}`),
              }))}
            />
            {/* Pour PayPal, la preuve est l'identifiant de transaction lu
                sur le relevé : c'est lui qu'on cherchera en cas de litige. */}
            <TextField
              label={
                method === 'paypal'
                  ? t('admin.orders.referencePaypal')
                  : t('admin.orders.reference')
              }
              hint={
                method === 'paypal'
                  ? t('admin.orders.referencePaypalHint')
                  : t('admin.orders.referenceHint')
              }
              value={reference}
              onChange={setReference}
            />
          </div>
          <Button
            size="sm"
            className="mt-4"
            disabled={busy}
            onClick={() =>
              markPaid.mutate(
                { method, reference: reference.trim() || null },
                {
                  onSuccess: (paid) => {
                    // Une table encaissée hors ligne n'écrit rien au client :
                    // la confirmation complète se propose aussitôt.
                    if (paid.status === 'paid' && paid.confirmation_kind === 'table') {
                      onPaidTable({ id: paid.id, order_number: paid.order_number })
                    }
                    onDone()
                  },
                },
              )
            }
          >
            {t('admin.orders.confirmPaid')}
          </Button>
        </section>
      ) : null}

      {refundable > 0 ? (
        <section className="mt-6 border-t border-line-subtle pt-5">
          <p className="text-caption uppercase tracking-wide text-content-muted">
            {t('admin.orders.refund')}
          </p>
          <p className="mt-1 text-xs text-content-muted">
            {t('admin.orders.refundable', { amount: money(refundable) })}
          </p>
          <div className="mt-3 grid gap-5 sm:grid-cols-2">
            <MoneyField
              label={t('admin.orders.refundAmount')}
              minor={refundAmount}
              currency={currency}
              onChange={setRefundAmount}
            />
            <TextField
              label={t('admin.orders.refundReason')}
              hint={t('admin.orders.refundReasonHint')}
              value={refundReason}
              onChange={setRefundReason}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-4"
            disabled={
              busy ||
              refundAmount === null ||
              refundAmount <= 0 ||
              refundAmount > refundable ||
              refundReason.trim().length < 5
            }
            onClick={() =>
              refund.mutate(
                { amount_minor: refundAmount ?? 0, reason: refundReason.trim() },
                { onSuccess: onDone },
              )
            }
          >
            {t('admin.orders.confirmRefund')}
          </Button>
        </section>
      ) : null}
    </div>
  )
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-content-muted">{label}</dt>
      <dd className={strong ? 'tabular-nums text-content-primary' : 'tabular-nums text-content-secondary'}>
        {value}
      </dd>
    </div>
  )
}

/**
 * L'échéancier d'une commande, et le geste de pointer une tranche encaissée.
 *
 * Le règlement arrive par virement PayPal : personne ne le confirme
 * automatiquement. Quelqu'un lit le relevé, reconnaît la référence, et le dit
 * ici. Chaque pointage est audité et nominatif.
 *
 * **Rien n'est émis tant qu'il reste une tranche due.** Le billet et le QR ne
 * partent qu'au pointage de la dernière : un acompte n'ouvre pas la porte.
 */
function InstalmentSchedule({
  order,
  eventId,
  money,
  onDone,
  onPaidTable,
}: {
  order: AdminOrder
  eventId: string | undefined
  money: Money
  onDone: () => void
  onPaidTable: (order: { id: string; order_number: string }) => void
}) {
  const { t, i18n } = useTranslation()
  const { event } = useAdminContext()
  // L'identifiant de la tranche voyage AVEC la mutation, pas dans un état :
  // l'adresse se construit au moment de l'appel, alors que `setState` ne
  // prend effet qu'au rendu suivant. On pointait ainsi la tranche cliquée
  // juste avant — l'acompte d'un client porté sur le solde d'un autre.
  const mark = useAdminMutation<AdminOrder, { method: string; installment_id: string }>(
    'post',
    (variables) =>
      `/admin/events/${eventId}/orders/${order.id}/installments/${variables.installment_id}/mark-paid`,
    eventId,
    [['admin-orders'], ['admin-reservations', eventId]],
  )

  if (order.installments.length === 0) {
    // Sans échéancier, il n'y a rien à pointer — mais c'est exactement là
    // qu'on veut pouvoir en poser un. Le panneau disparaissait, et le geste
    // n'existait nulle part.
    return (
      <InstalmentPlanner
        order={order}
        eventId={eventId}
        money={money}
        onDone={onDone}
      />
    )
  }

  const remaining = order.installments.filter((row) => row.status === 'pending')

  return (
    <section className="mt-6 border-t border-line-subtle pt-5">
      <p className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.orders.schedule')}
      </p>
      {remaining.length > 0 ? (
        <p className="mt-1 text-xs text-content-muted">{t('admin.orders.scheduleHint')}</p>
      ) : null}

      <ul className="mt-3 space-y-2">
        {order.installments.map((row) => (
          <li
            key={row.id}
            className={cn(
              'flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3',
              row.overdue
                ? 'border-state-danger/40 bg-state-danger/5'
                : row.status === 'paid'
                  ? 'border-state-success/40 bg-state-success/5'
                  : 'border-line-subtle',
            )}
          >
            <div className="min-w-0">
              <p className="text-sm text-content-primary">
                {t('admin.orders.instalment', { index: row.sequence })}
                {row.amount_minor !== null ? (
                  <>
                    {' · '}
                    <span className="tabular-nums">{money(row.amount_minor)}</span>
                  </>
                ) : null}
              </p>
              <p className="mt-0.5 text-xs text-content-muted">
                {row.status === 'paid' && row.paid_at
                  ? t('admin.orders.paidOn', {
                      date: formatDateTime(row.paid_at, i18n.language, event?.timezone ?? 'UTC'),
                    })
                  : t('admin.orders.dueOn', {
                      date: formatDateTime(row.due_at, i18n.language, event?.timezone ?? 'UTC'),
                    })}
                {row.overdue ? ` · ${t('admin.orders.settlement.overdue')}` : ''}
              </p>
            </div>

            {row.status === 'pending' ? (
              <Button
                size="sm"
                disabled={mark.isPending}
                onClick={() => {
                  // `method` part dans le journal d'audit : c'est la preuve
                  // sur laquelle l'encaissement a été constaté.
                  mark.mutate(
                    { method: 'transfer', installment_id: row.id },
                    {
                      onSuccess: (paid) => {
                        // La dernière tranche solde la table : même question
                        // qu'après « Encaisser hors ligne ».
                        if (paid.status === 'paid' && paid.confirmation_kind === 'table') {
                          onPaidTable({ id: paid.id, order_number: paid.order_number })
                        }
                        onDone()
                      },
                    },
                  )
                }}
              >
                {t('admin.orders.markInstalment')}
              </Button>
            ) : (
              <span className="text-xs text-content-muted">
                {t(`admin.orders.instalmentStatus.${row.status}`)}
              </span>
            )}
          </li>
        ))}
      </ul>

      {mark.error instanceof ApiError ? (
        <p className="mt-3 text-sm text-state-danger">{mark.error.message}</p>
      ) : null}
    </section>
  )
}


/**
 * Poser un échéancier sur une commande, à la demande.
 *
 * Le tunnel public propose déjà de régler une table en deux fois quand la
 * soirée est assez loin ; cet échéancier-là apparaît plus haut. Celui-ci sert
 * au cas par cas : une commande sans table, d'autres dates, d'autres
 * montants. Quelqu'un appelle, on s'arrange, et on l'inscrit ici.
 *
 * Deux tranches par défaut, la moitié chacune — c'est l'arrangement courant,
 * et la moitié restante est ce qu'on propose neuf fois sur dix. Les montants
 * comme les dates se corrigent, mais **la somme doit faire le total** : le
 * serveur le vérifie, et l'écran le dit avant d'envoyer plutôt qu'après.
 */
function InstalmentPlanner({
  order,
  eventId,
  money,
  onDone,
}: {
  order: AdminOrder
  eventId: string | undefined
  money: Money
  onDone: () => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // Sans les montants, impossible d'étaler un total qu'on ne voit pas : le
  // geste disparaît (l'écran est à qui voit l'argent).
  const total = order.total_minor ?? 0
  const half = Math.floor(total / 2)
  const today = new Date()
  const inDays = (days: number) =>
    new Date(today.getTime() + days * 86_400_000).toISOString().slice(0, 10)

  const [rows, setRows] = useState([
    { amount: (total - half) / 100, due: inDays(2) },
    { amount: half / 100, due: inDays(21) },
  ])

  const create = useAdminMutation<
    AdminOrder,
    { entries: { amount_minor: number; due_at: string }[] }
  >(
    'put',
    () => `/admin/events/${eventId}/orders/${order.id}/installments`,
    eventId,
    [['admin-orders']],
  )

  // `Math.round` obligatoire : 30.00 * 100 peut valoir 2999,999… en virgule
  // flottante, et une tranche à 29,99 € n'est pas celle qu'on a saisie.
  const minor = (value: number) => Math.round(value * 100)
  const sum = rows.reduce((total, row) => total + minor(row.amount || 0), 0)
  const balanced = sum === total

  // Seule une commande qui attend son règlement s'étale : une commande
  // payée, annulée ou expirée n'a plus d'échéance à poser.
  if (order.status !== 'pending_payment' || order.total_minor === null) return null


  if (!open) {
    return (
      <section className="mt-6 border-t border-line-subtle pt-5">
        <p className="text-caption uppercase tracking-wide text-content-muted">
          {t('admin.orders.schedule')}
        </p>
        <p className="mt-1 text-xs text-content-muted">{t('admin.orders.planHint')}</p>
        <Button size="sm" variant="outline" className="mt-3" onClick={() => setOpen(true)}>
          {t('admin.orders.planCreate')}
        </Button>
      </section>
    )
  }

  return (
    <section className="mt-6 border-t border-line-subtle pt-5">
      <p className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.orders.schedule')}
      </p>

      <ul className="mt-3 grid gap-2">
        {rows.map((row, index) => (
          <li key={index} className="flex flex-wrap items-center gap-2">
            <span className="w-20 shrink-0 text-sm text-content-secondary">
              {t('admin.orders.instalment', { index: index + 1 })}
            </span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={row.amount}
              onChange={(event) =>
                setRows((current) =>
                  current.map((entry, position) =>
                    position === index
                      ? { ...entry, amount: Number(event.target.value) }
                      : entry,
                  ),
                )
              }
              className="w-28 rounded-md border border-line bg-bg-base px-2 py-1.5 text-right text-sm tabular-nums text-content-primary"
            />
            <input
              type="date"
              value={row.due}
              onChange={(event) =>
                setRows((current) =>
                  current.map((entry, position) =>
                    position === index ? { ...entry, due: event.target.value } : entry,
                  ),
                )
              }
              className="rounded-md border border-line bg-bg-base px-2 py-1.5 text-sm text-content-primary"
            />
            {rows.length > 2 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  setRows((current) => current.filter((_, position) => position !== index))
                }
              >
                {t('admin.orders.planRemove')}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>

      {rows.length < 6 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2"
          onClick={() => setRows((current) => [...current, { amount: 0, due: inDays(40) }])}
        >
          {t('admin.orders.planAdd')}
        </Button>
      ) : null}

      {/* Le déséquilibre se voit AVANT d'envoyer : découvrir le refus après
          coup obligerait à tout ressaisir. */}
      <p
        className={cn(
          'mt-3 text-sm tabular-nums',
          balanced ? 'text-content-secondary' : 'text-state-danger',
        )}
      >
        {t('admin.orders.planTotal', {
          sum: money(sum),
          total: money(order.total_minor),
        })}
      </p>

      {create.error ? (
        <p className="mt-2 text-sm text-state-danger">{create.error.message}</p>
      ) : null}

      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          disabled={!balanced || create.isPending}
          onClick={() =>
            create.mutate(
              {
                entries: rows.map((row) => ({
                  amount_minor: minor(row.amount),
                  due_at: new Date(`${row.due}T12:00:00Z`).toISOString(),
                })),
              },
              { onSuccess: onDone },
            )
          }
        >
          {t('admin.orders.planSave')}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </section>
  )
}
